import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { ScavengerItem, Submission, Stats } from '../types';
import { scavengerService, authService } from '../services/api';
import { processImageFile } from '../utils/imageCompression';
import ItemCard from './ItemCard';
import StatsCard from './StatsCard';
import Rules from './Rules';
import './Dashboard.css';
import map from '../images/map.jpg'

// Editable team name component
interface EditableTeamNameProps {
  initialName: string;
  onSave: (name: string) => void;
  onCancel: () => void;
}

const EditableTeamName: React.FC<EditableTeamNameProps> = ({ initialName, onSave, onCancel }) => {
  const [name, setName] = useState(initialName);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave(name);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      onCancel();
    }
  };

  return (
    <form onSubmit={handleSubmit} className="editable-team-name">
      <input
        type="text"
        value={name}
        onChange={(e) => setName(e.target.value)}
        onKeyDown={handleKeyDown}
        onBlur={() => onSave(name)}
        placeholder="Enter custom team name"
        maxLength={50}
        autoFocus
        className="team-name-input"
      />
    </form>
  );
};

// Team picture component
interface TeamPictureProps {
  currentPicture?: string | null;
  onUpload: (file: File) => void;
  onRemove: () => void;
  uploading?: boolean;
}

const TeamPicture: React.FC<TeamPictureProps> = ({ currentPicture, onUpload, onRemove, uploading }) => {
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Validation will be handled in the parent component
      onUpload(file);
    }
    // Reset the input so the same file can be selected again if needed
    if (e.target) {
      e.target.value = '';
    }
  };

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  return (
    <div className="team-picture-container">
      <div className="team-picture-display">
        {currentPicture ? (
          <img 
            src={`/api/uploads/${currentPicture}`} 
            alt="Team" 
            className="team-picture-img"
          />
        ) : (
          <div className="team-picture-placeholder">
            👥
            <span>No team picture</span>
          </div>
        )}
        
        {uploading && (
          <div className="team-picture-uploading">
            <div className="uploading-spinner">⏳</div>
          </div>
        )}
      </div>
      
      <div className="team-picture-actions">
        <input
          type="file"
          ref={fileInputRef}
          onChange={handleFileSelect}
          accept="image/*,.heic,.heif"
          style={{ display: 'none' }}
        />
        
        <button
          onClick={handleUploadClick}
          disabled={uploading}
          className="team-picture-upload-btn"
          title={currentPicture ? "Change team picture" : "Upload team picture"}
        >
          📷 {currentPicture ? "Change" : "Upload"}
        </button>
        
        {currentPicture && (
          <button
            onClick={onRemove}
            disabled={uploading}
            className="team-picture-remove-btn"
            title="Remove team picture"
          >
            🗑️ Remove
          </button>
        )}
      </div>
    </div>
  );
};



const Dashboard: React.FC = () => {
  const { state, logout } = useAuth();
  const [items, setItems] = useState<ScavengerItem[]>([]);
  const [submissions, setSubmissions] = useState<Submission[]>([]);
  const [stats, setStats] = useState<Stats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState<'all' | 'completed' | 'pending'>('all');
  const [sortBy, setSortBy] = useState<'id' | 'score'>('id');
  const [searchTerm, setSearchTerm] = useState('');
  const [isHuntmasterView, setIsHuntmasterView] = useState(false);
  const [customTeamName, setCustomTeamName] = useState('');
  const [isEditingTeamName, setIsEditingTeamName] = useState(false);
  const [teamPicture, setTeamPicture] = useState<string | null>(null);
  const [uploadingPicture, setUploadingPicture] = useState(false);
  const [submissionsEnabled, setSubmissionsEnabled] = useState(true);
  const [scoresReleased, setScoresReleased] = useState(false);

  const handleBackToHuntmaster = () => {
    // Clear the redirect session flag
    sessionStorage.removeItem('huntmaster_redirect_session');
    
    // Store huntmaster login info for auto-login
    const huntmasterLoginInfo = { 
      teamId: '999', 
      passcode: 'huntmaster', 
      timestamp: Date.now() 
    };
    
    // Use localStorage for cross-tab communication, then redirect
    localStorage.setItem('huntmaster_team_redirect', JSON.stringify(huntmasterLoginInfo));
    
    // Redirect to main page which will auto-login as huntmaster
    window.location.href = window.location.origin;
  };

  useEffect(() => {
    loadData();
    
    // Check if this session was created by huntmaster redirect (with small delay to ensure flag is set)
    setTimeout(() => {
      const wasRedirectedByHuntmaster = sessionStorage.getItem('huntmaster_redirect_session');
      console.log('Checking huntmaster redirect flag:', wasRedirectedByHuntmaster);
      if (wasRedirectedByHuntmaster) {
        setIsHuntmasterView(true);
      } else {
        setIsHuntmasterView(false);
      }
    }, 100);
    
    // Load team name from database - use actual database name, not localStorage
    if (state.group?.name && state.group.name !== 'xxxx') {
      setCustomTeamName(state.group.name);
    } else {
      setCustomTeamName('');
    }

    // Load team picture if available
    if (state.group?.teamPicture) {
      setTeamPicture(state.group.teamPicture);
    }
  }, [state.group?.id, state.group?.teamPicture, state.group?.name]);

  // Custom team name management functions
  const saveCustomTeamName = async (name: string) => {
    if (!state.group?.id) return;
    
    try {
      const teamId = typeof state.group.id === 'string' ? parseInt(state.group.id) : state.group.id;
      
      if (name.trim()) {
        // Save to database via API
        await authService.updateTeamName(teamId, name.trim());
        setCustomTeamName(name.trim());
      } else {
        // Reset to original team name if custom name is cleared
        const originalName = `xxxx`;
        await authService.updateTeamName(teamId, originalName);
        setCustomTeamName('');
      }
    } catch (error) {
      console.error('Failed to save team name:', error);
      // Set error state to show user that save failed
      setError('Failed to save team name. Please try again.');
    }
  };

  const handleTeamNameEdit = () => {
    setIsEditingTeamName(true);
  };

  const handleTeamNameSave = async (name: string) => {
    await saveCustomTeamName(name);
    setIsEditingTeamName(false);
  };

  const handleTeamNameCancel = () => {
    setIsEditingTeamName(false);
  };

  const handleTeamPictureUpload = async (file: File) => {
    setUploadingPicture(true);
    try {
      // Basic file validation (same as ItemCard)
      const isHeic = file.type === 'image/heic' || file.type === 'image/heif' || 
                     file.name.toLowerCase().endsWith('.heic') || file.name.toLowerCase().endsWith('.heif');
      
      if (!file.type.startsWith('image/') && !isHeic) {
        setError('Please select an image file (including HEIC/HEIF)');
        return;
      }

      // Validate file size (max 10MB before processing)
      if (file.size > 10 * 1024 * 1024) {
        setError('File size must be less than 10MB');
        return;
      }

      // Process the image (HEIC conversion + compression) - optimized for team pictures
      const processedFile = await processImageFile(file, 800, 0.8);
      
      const response = await authService.uploadTeamPicture(processedFile);
      setTeamPicture(response.filename);
    } catch (error: any) {
      setError(error.message || 'Failed to upload team picture');
    } finally {
      setUploadingPicture(false);
    }
  };

  const handleTeamPictureRemove = async () => {
    setUploadingPicture(true);
    try {
      await authService.deleteTeamPicture();
      setTeamPicture(null);
    } catch (error: any) {
      setError(error.message || 'Failed to remove team picture');
    } finally {
      setUploadingPicture(false);
    }
  };

  const getDisplayTeamName = () => {
    // If there's a custom name, use it
    if (customTeamName.trim()) {
      return customTeamName;
    }
    
    // Use the actual group name from the database (which will be "xxxx" by default)
    return state.group?.name || 'xxxx';
  };

  const loadData = async () => {
    try {
      setLoading(true);
      const [itemsData, submissionsData, statsData, submissionStatusData, scoreReleaseStatusData] = await Promise.all([
        scavengerService.getItems(),
        scavengerService.getSubmissions(),
        scavengerService.getStats(),
        scavengerService.getSubmissionStatus(),
        scavengerService.getScoreReleaseStatus()
      ]);
      
      setItems(itemsData);
      setSubmissions(submissionsData);
      setStats(statsData);
      setSubmissionsEnabled(submissionStatusData.enabled);
      setScoresReleased(scoreReleaseStatusData.released);
    } catch (error: any) {
      setError('Failed to load data');
      console.error('Load data error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleImageSubmit = async (itemId: number, file: File) => {
    try {
      // Basic file validation (same as ItemCard)
      const isHeic = file.type === 'image/heic' || file.type === 'image/heif' || 
                     file.name.toLowerCase().endsWith('.heic') || file.name.toLowerCase().endsWith('.heif');
      
      if (!file.type.startsWith('image/') && !isHeic) {
        throw new Error('Please select an image file (including HEIC/HEIF)');
      }

      // Validate file size (max 10MB before processing)
      if (file.size > 10 * 1024 * 1024) {
        throw new Error('File size must be less than 10MB');
      }

      // Process the image (HEIC conversion + compression)
      const processedFile = await processImageFile(file, 1200, 0.8);
      
      await scavengerService.submitImage(itemId, processedFile);
      await loadData(); // Refresh data
    } catch (error: any) {
      throw new Error(error.response?.data?.error || error.message || 'Failed to submit image');
    }
  };

  const getFilteredItems = () => {
    let filteredItems = [...items];
    
    // Apply search filter
    if (searchTerm.trim()) {
      filteredItems = filteredItems.filter(item =>
        item.description.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    // Apply status filter
    if (filter === 'completed') {
      filteredItems = filteredItems.filter(item => 
        submissions.some(sub => sub.itemId === item.id)
      );
    } else if (filter === 'pending') {
      filteredItems = filteredItems.filter(item => 
        !submissions.some(sub => sub.itemId === item.id)
      );
    }
    
    // Apply sort
    filteredItems.sort((a, b) => {
      if (sortBy === 'score') {
        return b.score - a.score;
      }
      return a.id - b.id;
    });
    
    return filteredItems;
  };

  const filteredItems = getFilteredItems();

  if (loading) {
    return (
      <div className="dashboard-container">
        <div className="loading-spinner">Loading...</div>
      </div>
    );
  }

  return (
    <div className="dashboard-container">
      <header className="dashboard-header">
        <div className="header-actions">
            {isHuntmasterView && (
              <button onClick={handleBackToHuntmaster} className="back-to-huntmaster-button">
                🏆 Back to Huntmaster
              </button>
            )}
            <button onClick={logout} className="logout-button">
              Logout
            </button>
          </div>
        <div className="header-content">
          <div className="header-info">
            <h1>BMD Scavenger Hunt</h1>
            <div className="team-info-header">
              <div className="team-title">Welcome to the Hunt!</div>
              <div className="team-identification">
                <div className="team-info-row">
                  <div className="team-id-display">
                    
                    <span className="team-id-number">
                      Team {state.group?.id || 'N/A'}: {isEditingTeamName ? (
                      <EditableTeamName 
                        initialName={customTeamName || ''}
                        onSave={handleTeamNameSave}
                        onCancel={handleTeamNameCancel}
                      />
                    ) : (
                      <span>
                        <span className={`team-name ${!getDisplayTeamName().toLowerCase().startsWith('team') && !getDisplayTeamName().toLowerCase().startsWith('xxx') ? 'custom-name' : ''}`}>
                          {getDisplayTeamName()}
                        </span>
                        <button 
                          className="edit-name-btn" 
                          onClick={handleTeamNameEdit}
                          title="Edit team name"
                        >
                          ✏️ 
                        </button>
                      </span>
                    )}
                    </span>
                  </div>
                 
                </div>
                
                <div className="team-picture-section">
                  <TeamPicture
                    currentPicture={teamPicture}
                    onUpload={handleTeamPictureUpload}
                    onRemove={handleTeamPictureRemove}
                    uploading={uploadingPicture}
                  />
                </div>
              </div>
            </div>
          </div>
          
        </div>
      </header>

      {error && (
        <div className="error-banner">
          {error}
          <button onClick={() => setError('')} className="error-close">×</button>
        </div>
      )}

      {!submissionsEnabled && (
        <div className="submissions-disabled-banner">
          🚫 Submissions are currently disabled by the Huntmaster
        </div>
      )}

      {stats && <StatsCard stats={stats} scoresReleased={scoresReleased} />}
      
      
      <div className="dashboard-controls">
        <Rules compact={true} />
      </div>
      {/* Hunt Instructions and Map Section */}
      <div className="hunt-instructions-section">
        <div className="hunt-instructions-header">
          <h3>📍 Hunt Location & Instructions</h3>
        </div>
        
        <div className="hunt-instructions-content">
          <p>
            All the items on the scavenger hunt should be able to be found in the area on the map below, 
            but you are welcome to find them anywhere you can - but not online. Have fun finding these items/places!
          </p>
          
          {/* <div className="map-placeholder-content"> */}
              {/* <div className="map-placeholder-icon">🗺️</div>
              <div className="map-placeholder-text">
                <p>Hunt Area Map</p>
                <span>Map will be displayed here</span>
              </div> */}
            {/* </div> */}
            {/* Replace this placeholder with actual map image */}
            <img src={map} alt="Scavenger Hunt Area Map" className="hunt-map-image" />
          
        </div>
      </div>
      <div className="dashboard-controls">
        <div className="search-controls">
          <label>Search:</label>
          <input
            type="text"
            placeholder="Search items..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
        </div>
        
        <div className="filter-controls">
          <label>Filter:</label>
          <select value={filter} onChange={(e) => setFilter(e.target.value as any)}>
            <option value="all">All Items ({items.length})</option>
            <option value="completed">Completed ({submissions.length})</option>
            <option value="pending">Pending ({items.length - submissions.length})</option>
          </select>
        </div>
        
        <div className="sort-controls">
          <label>Sort by:</label>
          <select value={sortBy} onChange={(e) => setSortBy(e.target.value as any)}>
            <option value="id">Item Number</option>
            <option value="score">Score (High to Low)</option>
          </select>
        </div>
      </div>

      <div className="upload-hint-banner">
        📷 Upload Guidelines: Max 10MB • Accepted format: JPG, PNG, GIF, HEIC/HEIF 
      </div>

      <div className="items-grid">
        {filteredItems.map(item => {
          const submission = submissions.find(sub => sub.itemId === item.id);
          return (
            <ItemCard
              key={item.id}
              item={item}
              submission={submission}
              onImageSubmit={handleImageSubmit}
              onImageDelete={loadData}
              submissionsEnabled={submissionsEnabled}
              scoresReleased={scoresReleased}
            />
          );
        })}
      </div>

      {filteredItems.length === 0 && (
        <div className="empty-state">
          <p>
            {searchTerm.trim() 
              ? `No items found matching "${searchTerm}".`
              : 'No items found for the selected filter.'
            }
          </p>
        </div>
      )}

      
    </div>
  );
};

export default Dashboard;
